import React, { createContext, useContext, useEffect, useState } from 'react';
import { ThemeProvider as NextThemeProvider } from 'next-themes';
import { useAutoTheme } from '@/hooks/useAutoTheme';

interface ThemeContextType {
  autoThemeEnabled: boolean;
  toggleAutoTheme: () => void;
  themeInfo: {
    isDayTime: boolean;
    currentHour: number;
    nextChange: string;
    currentMode: string;
  };
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

const AutoThemeManager = ({ children }: { children: React.ReactNode }) => {
  const [autoThemeEnabled, setAutoThemeEnabled] = useState(true);
  const { getCurrentThemeInfo, theme } = useAutoTheme();

  // Solo activar el auto-theme si está habilitado
  useEffect(() => {
    // El hook useAutoTheme ya maneja la lógica automática
    // Aquí solo guardamos la preferencia del usuario
    const saved = localStorage.getItem('auto-theme-enabled');
    if (saved !== null) {
      setAutoThemeEnabled(JSON.parse(saved));
    }
  }, []);

  const toggleAutoTheme = () => {
    const newValue = !autoThemeEnabled;
    setAutoThemeEnabled(newValue);
    localStorage.setItem('auto-theme-enabled', JSON.stringify(newValue));
  };

  const themeInfo = getCurrentThemeInfo();

  return (
    <ThemeContext.Provider
      value={{
        autoThemeEnabled,
        toggleAutoTheme,
        themeInfo
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  return (
    <NextThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem={false}
      disableTransitionOnChange={false}
    >
      <AutoThemeManager>
        {children}
      </AutoThemeManager>
    </NextThemeProvider>
  );
};